import defaultOgImageJpg from '../../assets/og-image.jpg';
import { canonical, description, join, meta, openGraph, title } from '@rea-argonaut/actions';
import { isNotNullOrUndefinedValue } from '../../helpers/isNotNullOrUndefinedValue';

const getGoogleTagManagerScripts = (): string => {
  /**
   * Google Tag script used to load ads on PCA
   * Async handling through use of googletag.cmd to queue callbacks for when GPT is ready
   * API Docs: https://developers.google.com/publisher-tag/guides/get-started
   */
  return `
    <script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>
    <script>
      window.googletag = window.googletag || {cmd: []};  
      googletag.cmd.push(function() {
        googletag.pubads().setForceSafeFrame(true);
        googletag.pubads().setSafeFrameConfig({ allowOverlayExpansion: false, allowPushExpansion: false , sandbox: true });
        googletag.pubads().disableInitialLoad();
        googletag.pubads().enableSingleRequest();
        googletag.enableServices();
      });
    </script> 
  `;
};

type Helmet = {
  canonical: string;
  title: string;
  description: string;
  siteName: string;
  ogImage: {
    url: string;
    alt: string | null;
    secure_url: string | null;
    width: string | null;
    height: string | null;
    type: string | null;
  } | null;
};

export const getHead = (helmet: Helmet | null) => {
  const { url: ogImageUrl = defaultOgImageJpg, ...ogImageFields } = helmet?.ogImage || {};

  return join(
    ...[
      helmet?.title && title(helmet?.title),
      helmet?.description && description(helmet?.description),
      helmet?.canonical && canonical(helmet?.canonical),
      openGraph({
        property: 'og:locale',
        content: 'en_AU',
      }),
      openGraph({
        property: 'og:type',
        content: 'website',
      }),
      helmet?.title &&
        openGraph({
          property: 'og:title',
          content: helmet?.title,
        }),
      helmet?.description &&
        openGraph({
          property: 'og:description',
          content: helmet?.description,
        }),
      helmet?.canonical &&
        openGraph({
          property: 'og:url',
          content: helmet?.canonical,
        }),
      helmet?.siteName &&
        openGraph({
          property: 'og:site_name',
          content: helmet?.siteName,
        }),
      openGraph({
        property: 'og:image',
        content: ogImageUrl,
      }),
      ...Object.entries(ogImageFields).map(
        ([key, value]) =>
          value &&
          openGraph({
            property: `og:image:${key}`,
            content: value,
          }),
      ),
      meta({
        name: 'thumbnail',
        content: ogImageUrl,
      }),
      meta({
        name: 'robots',
        content: 'index, follow, noodp',
      }),
      getGoogleTagManagerScripts(),
    ].filter(isNotNullOrUndefinedValue),
  );
};
